'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { useCookies } from 'next-client-cookies';
import { ProjectSettings } from 'shared/types/ProjectSettings';
import { useRedirectToGateway } from 'components/commercetools-ui/atoms/iframe-guard/useRedirectToGateway';
import { AccountContext } from 'context/account';
import { usePathname, useRouter } from 'i18n/routing';
import { usePageLoader } from 'providers/loader';
import { sdk } from 'sdk';
import { GetAccountActionReturn } from 'sdk/composable-commerce/types/actions/AccountActions';

type Props = {
  projectSettings: SDKResponse<ProjectSettings>;
  account: SDKResponse<GetAccountActionReturn>;
  isBot: boolean;
  children: React.ReactNode;
};

export default function GmsbHandoffManager({ projectSettings, children, account, isBot }: Readonly<Props>) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const cookies = useCookies();
  const locale = cookies.get('locale') ?? 'en-us';

  sdk.defaultConfigure(locale);

  const { gmsblogin, loggedIn } = useContext(AccountContext);
  const { showLoader, hideLoader } = usePageLoader();

  const redirectToGateway = useRedirectToGateway();

  const token = searchParams.get('token');

  const attemptedRef = useRef<string | null>(null);
  const processingRef = useRef(false);

  const [needsStorageAccess, setNeedsStorageAccess] = useState(false);
  const [isReady, setIsReady] = useState(!token);

  const fallbackRedirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { PUBLIC_DEEP_LINK = 'https://fujifilm-google-photos-dev.ffvoyager.site/' } = projectSettings.isError
    ? {}
    : projectSettings.data;

  const justLoggedInRef = useRef(false);

  /**
   * Clear transition flag once context reflects loggedIn
   */
  useEffect(() => {
    if (loggedIn && justLoggedInRef.current) {
      justLoggedInRef.current = false;
    }
  }, [loggedIn]);

  /**
   * Route protection.
   */
  useEffect(() => {
    if (isBot || pathname.startsWith('/__preview') || pathname.startsWith('/static')) {
      return;
    }

    // Skip protection if token is in URL OR if user just logged in
    if (token || justLoggedInRef.current) {
      return;
    }

    const hasAccess = loggedIn || (!account.isError && account.data.loggedIn);

    if (!hasAccess) {
      redirectToGateway({
        pathname: pathname || '/',
        searchParams,
        gatewayUrl: PUBLIC_DEEP_LINK,
      });
    }
  }, [pathname, searchParams, isBot, account, loggedIn, token, redirectToGateway, PUBLIC_DEEP_LINK]);

  const handleRedirect = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());

    params.delete('token');

    redirectToGateway({
      pathname,
      searchParams: params,
      gatewayUrl: PUBLIC_DEEP_LINK,
    });
  }, [pathname, searchParams, redirectToGateway, PUBLIC_DEEP_LINK]);

  /**
   * No token = no handoff flow.
   */
  useEffect(() => {
    if (!token) {
      setIsReady(true);
    }
  }, [token]);

  /**
   * Determine whether browser storage access is available.
   */
  useEffect(() => {
    if (!token) return;

    const verifyAccess = async () => {
      try {
        const testName = 'test_partitioned';

        document.cookie = `${testName}=1; SameSite=None; Secure; Partitioned; Max-Age=10`;

        const hasPartitionedSupport = document.cookie.includes(`${testName}=1`);

        document.cookie = `${testName}=; Max-Age=0; SameSite=None; Secure; Partitioned`;

        if (hasPartitionedSupport) {
          setNeedsStorageAccess(false);
          return;
        }
      } catch {
        // fallback to Storage Access API
      }

      if (typeof document !== 'undefined' && 'hasStorageAccess' in document) {
        const hasAccess = await document.hasStorageAccess();

        setNeedsStorageAccess(!hasAccess);
      }
    };

    void verifyAccess();
  }, [token]);

  /**
   * Global loader.
   */
  useEffect(() => {
    if (!token || isReady) return;

    if (needsStorageAccess) {
      hideLoader();
      return;
    }

    showLoader();
  }, [token, isReady, needsStorageAccess, showLoader, hideLoader]);

  /**
   * Cleanup.
   */
  useEffect(() => {
    return () => {
      hideLoader();

      if (fallbackRedirectTimeoutRef.current) {
        clearTimeout(fallbackRedirectTimeoutRef.current);
      }
    };
  }, [hideLoader]);

  /**
   * Auto-login flow.
   */
  useEffect(() => {
    if (!token) return;

    const autoLogin = async () => {
      if (processingRef.current || needsStorageAccess || attemptedRef.current === token) {
        return;
      }

      processingRef.current = true;
      attemptedRef.current = token;

      try {
        const response = await gmsblogin(token);

        if (response.isError) {
          attemptedRef.current = null;
          hideLoader();

          fallbackRedirectTimeoutRef.current ??= setTimeout(() => {
            fallbackRedirectTimeoutRef.current = null;
            handleRedirect();
          }, 3000);

          return;
        }
        // Mark transition flag before stripping token parameter
        justLoggedInRef.current = true;

        const params = new URLSearchParams(searchParams.toString());
        params.delete('token');

        const nextUrl = params.toString().length > 0 ? `${pathname}?${params.toString()}` : pathname;

        hideLoader();
        router.replace(nextUrl);
      } catch (error) {
        console.error('GMSB auto-login failed', error);
        attemptedRef.current = null;
        hideLoader();

        fallbackRedirectTimeoutRef.current ??= setTimeout(() => {
          fallbackRedirectTimeoutRef.current = null;
          handleRedirect();
        }, 3000);
      } finally {
        processingRef.current = false;
      }
    };

    void autoLogin();
  }, [token, needsStorageAccess, gmsblogin, pathname, router, searchParams, hideLoader, handleRedirect]);

  /**
   * User grants Storage Access.
   */
  const handleGrantAccess = async () => {
    try {
      await document.requestStorageAccess();

      setNeedsStorageAccess(false);
    } catch (error) {
      console.error('Storage access request failed', error);
    }
  };

  if (token && needsStorageAccess) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
        <div className="max-w-md rounded-lg border p-8 shadow-sm">
          <h1 className="mb-4 text-xl font-bold">{'Action Required'}</h1>

          <p className="mb-6 text-gray-600">
            {'To log you in securely, we need permission to access browser cookies.'}
          </p>

          <button
            type="button"
            onClick={handleGrantAccess}
            className="rounded-full bg-[#01916D] px-8 py-3 font-bold text-white transition-opacity hover:opacity-90"
          >
            {'Allow and Continue'}
          </button>
        </div>
      </div>
    );
  }

  /**
   * Block children until login completes.
   */
  if (token && !isReady) {
    return null;
  }

  return <>{children}</>;
}
